'use client';

import { FC, PropsWithChildren, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { getProject } from '@hub/helpers/get-project';
import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  useColorScheme,
} from '@mui/material';
import { Theme } from '@monorepo/client-types';
import { ThemeContext } from '@monorepo/hub-common';
import { createAppTheme } from '@monorepo/nova-ui';
import { useStore } from '../helpers/use-store';
import { EnvService } from '../services/core/env-service';
import { AntdProvider } from './antd-provider';

const { application } = EnvService;

const muiTheme = createAppTheme(application);

const ThemeContextProvider: FC<PropsWithChildren> = observer(({ children }) => {
  const { themeStore } = useStore();
  const { mode, setMode } = useColorScheme();
  const { is5ers, isTtp } = getProject();

  useEffect(() => {
    const antdTheme = themeStore.currentTheme;
    setMode(antdTheme); // Sync MUI theme with Antd theme on load
  }, []);

  const toggleTheme = () => {
    if (mode === Theme.Light) {
      themeStore.switchTheme(Theme.Dark);
      setMode(Theme.Dark);
    } else {
      themeStore.switchTheme(Theme.Light);
      setMode(Theme.Light);
    }
  };

  const value = { toggleTheme, is5ers, isTtp, isVite: true, isNextJs: false };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
});

export const ThemeProvider: FC<PropsWithChildren> = observer(({ children }) => {
  return (
    <AntdProvider>
      <MuiThemeProvider theme={muiTheme} forceThemeRerender>
        <CssBaseline />
        <ThemeContextProvider>{children}</ThemeContextProvider>
      </MuiThemeProvider>
    </AntdProvider>
  );
});
